import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'

/**
 * Calculate nested orbits - Chapter 1: Multidimensional Human
 */
function calcNestedOrbits(audioData, time) {
  const { bass = 0, amplitude = 0 } = audioData
  const positions = []
  const radii = [2, 3.5, 5]
  const speeds = [1, 0.7, 0.5]
  const verticalOffsets = [0, 0.5, 1]

  radii.forEach((radius, radiusIndex) => {
    for (let i = 0; i < 3; i++) {
      const angle = (time * speeds[radiusIndex] + (i * Math.PI * 2) / 3) % (Math.PI * 2)
      const effectiveRadius = radius * (1 + bass * 0.3)
      positions.push({
        x: Math.cos(angle) * effectiveRadius,
        y: verticalOffsets[radiusIndex] + Math.sin(time * 2 + i) * 0.2 + amplitude * 0.5,
        z: Math.sin(angle) * effectiveRadius
      })
    }
  })
  return positions
}

/**
 * Calculate vesica dance - Chapter 2: Language of Coherence
 */
function calcVesicaDance(audioData, time) {
  const { mids = 0 } = audioData
  const positions = []
  for (let i = 0; i < 9; i++) {
    const t = i / 9
    const angle = t * Math.PI * 2 + time * 0.5
    const r = 3 + Math.sin(angle * 2 + time) * (1 + mids)
    positions.push({
      x: Math.cos(angle) * r,
      y: Math.sin(time + i * 0.5) * 1.5,
      z: Math.sin(angle) * r
    })
  }
  return positions
}

/**
 * Calculate flower rotation - Chapter 3: Sacred Geometry
 */
function calcFlowerRotation(audioData, time) {
  const { highs = 0 } = audioData
  const positions = []
  const phi = (1 + Math.sqrt(5)) / 2
  for (let i = 0; i < 9; i++) {
    const angle = i * Math.PI * 2 * phi + time * 0.3
    const r = 3 + i * 0.3
    positions.push({
      x: Math.cos(angle) * r * (1 + highs * 0.2),
      y: Math.sin(time * 0.5 + i) * 0.8,
      z: Math.sin(angle) * r * (1 + highs * 0.2)
    })
  }
  return positions
}

/**
 * Calculate aurora swarm - Chapter 4: Plasma Consciousness
 */
function calcAuroraSwarm(audioData, time) {
  const { amplitude = 0 } = audioData
  const positions = []
  for (let i = 0; i < 9; i++) {
    const t = i / 9
    positions.push({
      x: Math.sin(time * 0.7 + t * Math.PI * 4) * (3 + amplitude * 2),
      y: Math.cos(time * 0.5 + t * Math.PI * 2) * 2 + t * 2 - 1,
      z: Math.sin(time * 0.9 + t * Math.PI * 3) * (3 + amplitude)
    })
  }
  return positions
}

/**
 * Calculate pendulum swing - Chapter 5: Hermetic Principles
 */
function calcPendulumSwing(audioData, time) {
  const { bass = 0 } = audioData
  const positions = []
  for (let i = 0; i < 9; i++) {
    const phase = (i / 9) * Math.PI
    const swing = Math.sin(time * 1.5 + phase) * (2 + bass)
    positions.push({
      x: swing,
      y: i * 0.5 - 2 + Math.cos(time + i) * 0.3,
      z: Math.cos(time * 0.8 + phase) * 3
    })
  }
  return positions
}

/**
 * Calculate body dance - Chapter 6: Movement Medicine
 */
function calcBodyDance(audioData, time) {
  const { mids = 0, beat = false } = audioData
  const positions = []
  const beatPulse = beat ? 1.3 : 1
  for (let i = 0; i < 9; i++) {
    const angle = (i / 9) * Math.PI * 2 + time
    positions.push({
      x: Math.sin(angle + Math.sin(time * 2) * mids) * 3 * beatPulse,
      y: Math.sin(time * 3 + i) * 1.5 + (i % 3 - 1),
      z: Math.cos(angle + Math.cos(time * 2) * mids) * 3 * beatPulse
    })
  }
  return positions
}

/**
 * Calculate cymatics formation - Chapter 7: Silent Vibrations
 */
function calcCymaticsFormation(audioData, time) {
  const { highs = 0 } = audioData
  const positions = []
  for (let i = 0; i < 9; i++) {
    const angle = (i / 9) * Math.PI * 2
    const r = 3 + Math.sin(angle * 3 + time * 2) * (1 + highs * 2)
    positions.push({
      x: Math.cos(angle) * r,
      y: Math.sin(angle * 4 + time) * 0.5,
      z: Math.sin(angle) * r
    })
  }
  return positions
}

/**
 * Calculate breath pulse - Chapter 8: Breath as Bridge
 */
function calcBreathPulse(audioData, time) {
  const { amplitude = 0 } = audioData
  const positions = []
  const breathCycle = (Math.sin(time * 0.5) + 1) / 2
  for (let i = 0; i < 9; i++) {
    const angle = (i / 9) * Math.PI * 2
    const r = 2 + breathCycle * 2 + amplitude
    positions.push({
      x: Math.cos(angle + time * 0.2) * r,
      y: Math.sin(time * 0.5 + i * 0.3) * breathCycle * 2,
      z: Math.sin(angle + time * 0.2) * r
    })
  }
  return positions
}

/**
 * Calculate neural fire - Chapter 9: Nervous System
 */
function calcNeuralFire(audioData, time) {
  const { beat = false, amplitude = 0 } = audioData
  const positions = []
  for (let i = 0; i < 9; i++) {
    const angle = (i / 9) * Math.PI * 2
    const jitter = beat ? Math.random() * 0.5 : 0
    positions.push({
      x: Math.cos(angle) * (3 + jitter) + Math.sin(time * 5 + i) * 0.2,
      y: Math.sin(time * 2 + i * 0.7) * 1.5 + amplitude,
      z: Math.sin(angle) * (3 + jitter) + Math.cos(time * 5 + i) * 0.2
    })
  }
  return positions
}

/**
 * Calculate heart emanation - Chapter 10: Love as Root
 */
function calcHeartEmanation(audioData, time) {
  const { bass = 0 } = audioData
  const positions = []
  for (let i = 0; i < 9; i++) {
    const t = i / 9
    const heartPhase = time * 1.2
    const r = 2 + Math.sin(heartPhase) * (1 + bass)
    const angle = t * Math.PI * 2 + heartPhase * 0.1
    positions.push({
      x: Math.cos(angle) * r * (1 + Math.sin(heartPhase + i) * 0.3),
      y: Math.sin(heartPhase * 2 + t * Math.PI) * 0.8,
      z: Math.sin(angle) * r * (1 + Math.sin(heartPhase + i) * 0.3)
    })
  }
  return positions
}

/**
 * Calculate network pulse - Chapter 11: Collective Consciousness
 */
function calcNetworkPulse(audioData, time) {
  const { amplitude = 0 } = audioData
  const positions = []
  for (let i = 0; i < 9; i++) {
    const gridX = (i % 3) - 1
    const gridZ = Math.floor(i / 3) - 1
    const pulse = Math.sin(time * 2 + i * 0.5) * (1 + amplitude)
    positions.push({
      x: gridX * 2.5 + Math.sin(time + i) * 0.3,
      y: pulse,
      z: gridZ * 2.5 + Math.cos(time + i) * 0.3
    })
  }
  return positions
}

/**
 * Calculate spiral ascent - Chapter 12: Transcendence
 */
function calcSpiralAscent(audioData, time) {
  const { highs = 0 } = audioData
  const positions = []
  for (let i = 0; i < 9; i++) {
    const t = i / 9
    const angle = t * Math.PI * 4 + time * 0.5
    const r = 2 + t * 2
    const rise = t * 4 - 2 + Math.sin(time + i) * 0.3
    positions.push({
      x: Math.cos(angle) * r * (1 + highs * 0.3),
      y: rise,
      z: Math.sin(angle) * r * (1 + highs * 0.3)
    })
  }
  return positions
}

/**
 * Calculate fractal orbit - Chapter 13: The Normal Map
 */
function calcFractalOrbit(audioData, time) {
  const { mids = 0 } = audioData
  const positions = []
  for (let i = 0; i < 9; i++) {
    const level = Math.floor(i / 3)
    const indexInLevel = i % 3
    const angle = (indexInLevel / 3) * Math.PI * 2 + time * (1 - level * 0.3)
    const r = (3 - level) * (1 + mids * 0.2)
    positions.push({
      x: Math.cos(angle) * r,
      y: level * 1.5 - 1.5 + Math.sin(time + i) * 0.2,
      z: Math.sin(angle) * r
    })
  }
  return positions
}

/**
 * Calculate journey progress - Chapter 14: Practical Protocols
 */
function calcJourneyProgress(audioData, time) {
  const { amplitude = 0 } = audioData
  const positions = []
  for (let i = 0; i < 9; i++) {
    const progress = (i / 8)
    const pathAngle = progress * Math.PI + time * 0.2
    positions.push({
      x: Math.sin(pathAngle) * (3 + progress * 2),
      y: Math.sin(time * 0.5 + i * 0.4) * 0.5 + progress * 2 - 1,
      z: Math.cos(pathAngle) * (3 + progress * 2) * (1 + amplitude * 0.3)
    })
  }
  return positions
}

// Map chapter IDs to calculation functions
const behaviorMap = {
  1: calcNestedOrbits,
  2: calcVesicaDance,
  3: calcFlowerRotation,
  4: calcAuroraSwarm,
  5: calcPendulumSwing,
  6: calcBodyDance,
  7: calcCymaticsFormation,
  8: calcBreathPulse,
  9: calcNeuralFire,
  10: calcHeartEmanation,
  11: calcNetworkPulse,
  12: calcSpiralAscent,
  13: calcFractalOrbit,
  14: calcJourneyProgress
}

/**
 * Hook that returns animated orbit positions for a chapter's poem nodes
 * @param {number} chapterIndex - 1-14
 * @param {object} audioData - { bass, mids, highs, amplitude, beat }
 * @returns {Array<{x, y, z}>} Array of 9 positions
 */
export function useOrbitBehavior(chapterIndex, audioData) {
  const timeRef = useRef(0)
  const positionsRef = useRef([])

  // Get the calculation function for this chapter
  const calcFunction = behaviorMap[chapterIndex] || calcNestedOrbits

  // Update positions on each frame
  useFrame((state, delta) => {
    timeRef.current = state.clock.elapsedTime
    positionsRef.current = calcFunction(audioData || {}, timeRef.current)
  })

  // Initialize with default positions
  if (positionsRef.current.length === 0) {
    positionsRef.current = calcFunction(audioData || {}, 0)
  }

  return positionsRef.current
}
